import { Controller } from '@hotwired/stimulus';
import * as bootstrap from 'bootstrap';

export default class extends Controller {
  static targets = ['exitConfirmModal'];

  open() {
    this.#stripAutoOpenParams();

    const offcanvasEl = this.element.querySelector('.offcanvas');
    if (offcanvasEl) {
      this.panelEl = offcanvasEl;
      this.panel = new bootstrap.Offcanvas(offcanvasEl);
      this.panel.show();
      this.#disableTurboCaching();
      this.#pushHistoryEntry();
      this.#installDismissGuards(offcanvasEl, 'hide.bs.offcanvas');

      offcanvasEl.addEventListener('hidden.bs.offcanvas', () => {
        document.activeElement?.blur();
        this.#dispose();
      }, {once: true});
      return;
    }

    const modalEl = this.element.querySelector('.modal:not([data-frame-modal-target="exitConfirmModal"])');
    if (modalEl) {
      this.panelEl = modalEl;
      this.panel = new bootstrap.Modal(modalEl);
      this.panel.show();
      this.#disableTurboCaching();
      this.#pushHistoryEntry();
      this.#installDismissGuards(modalEl, 'hide.bs.modal');

      modalEl.addEventListener('hidden.bs.modal', () => {
        document.activeElement?.blur();
        this.#dispose();
      }, {once: true});
    }
  }

  #stripAutoOpenParams() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('finalize') && !url.searchParams.has('invested')) {
      return;
    }

    url.searchParams.delete('finalize');
    url.searchParams.delete('invested');
    history.replaceState(history.state, '', url.toString());
  }

  // A Back press while the panel is open makes Turbo cacheSnapshot() the page
  // with the backdrop still in the DOM; no-cache stops it from restoring that
  // stale snapshot (grey overlay) when leaving the funnel.
  #disableTurboCaching() {
    if (this.cacheControlMeta) return;

    const meta = document.createElement('meta');
    meta.name = 'turbo-cache-control';
    meta.content = 'no-cache';
    document.head.appendChild(meta);
    this.cacheControlMeta = meta;
  }

  #enableTurboCaching() {
    if (!this.cacheControlMeta) return;

    this.cacheControlMeta.remove();
    this.cacheControlMeta = null;
  }

  teardown() {
    if (!this.panel) return;
    // Only tear down when actually leaving via a Turbo visit. The Back trap
    // also fires turbo:before-cache without navigating — disposing there would
    // kill the popstate handler before it can show the confirmation.
    if (!this.navigating) return;

    document.activeElement?.blur();
    this.forceHide = true;
    this.#dispose();
  }

  #installDismissGuards(rootEl, hideEventName) {
    // Bootstrap's [data-bs-dismiss] handler is delegated on `document` in the
    // capture phase; we intercept the close button earlier (`window` is hit
    // first in capture) and stop the event so Bootstrap never hides the panel.
    this.boundDismissClick = (event) => {
      const trigger = event.target.closest('[data-bs-dismiss]');
      if (!trigger || !rootEl.contains(trigger)) return;
      if (!this.#exitConfirmationRequired(rootEl)) return;

      event.preventDefault();
      event.stopPropagation();
      this.#openExitConfirmation();
    };
    window.addEventListener('click', this.boundDismissClick, true);

    // Block backdrop dismiss on a locked step; Escape and programmatic hides
    // pass through via forceHide.
    this.boundHideGuard = (event) => {
      if (this.forceHide) {
        this.forceHide = false;
        return;
      }
      if (this.#isCurrentStepLocked(rootEl)) {
        event.preventDefault();
      }
    };
    rootEl.addEventListener(hideEventName, this.boundHideGuard);

    this.boundEscape = (event) => {
      if (event.key === 'Escape') this.forceHide = true;
    };
    rootEl.addEventListener('keydown', this.boundEscape);
  }

  #visibleStep(rootEl) {
    const steps = rootEl.querySelectorAll('[data-modal-steps-target="step"]');
    return Array.from(steps).find((step) => (
      !step.classList.contains('d-none')
    ));
  }

  #isCurrentStepLocked(rootEl) {
    return this.#visibleStep(rootEl)?.dataset.locked === 'true';
  }

  #exitConfirmationRequired(rootEl) {
    if (!this.hasExitConfirmModalTarget) return false;

    return this.#visibleStep(rootEl)?.dataset.exitConfirmation === 'true';
  }

  #openExitConfirmation() {
    this.exitConfirmation = bootstrap.Modal.getOrCreateInstance(this.exitConfirmModalTarget);
    this.exitConfirmModalTarget.addEventListener('hidden.bs.modal', () => {
      // Confirm: hide the panel only once the modal has fully transitioned out,
      // otherwise Bootstrap disposes its element mid-animation and throws.
      if (this.confirming) {
        this.confirming = false;
        this.forceHide = true;
        this.panel?.hide();
        return;
      }

      // Back: panel stays open — re-lock the body scroll Bootstrap just released.
      if (this.panel) document.body.style.overflow = 'hidden';
    }, {once: true});
    this.exitConfirmation.show();
  }

  confirmExit() {
    if (!this.exitConfirmation) {
      if (!this.panel) return;

      this.forceHide = true;
      this.panel.hide();
      return;
    }

    this.confirming = true;
    this.exitConfirmation.hide();
  }

  #releaseBodyScrollLock() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    document.body.classList.remove('modal-open');
  }

  #pushHistoryEntry() {
    // Trap the Back button with two synthetic entries carrying no Turbo
    // restorationIdentifier: Back lands on `guard`, which Turbo ignores (it only
    // restores on popstate state with a `turbo` key), so the frame survives and
    // our handler runs.
    history.pushState({frameModal: 'guard'}, '', window.location.href);
    history.pushState({frameModal: 'open'}, '', window.location.href);
    this.historyPushed = true;

    this.boundPopState = () => {
      if (this.suppressPopState) {
        this.suppressPopState = false;
        return;
      }
      if (!this.panel) return;

      // Turbo stamps its restoration state onto `guard` without navigating;
      // overwrite it back and re-push `open` so a later Back is trapped too.
      history.replaceState({frameModal: 'guard'}, '', window.location.href);
      history.pushState({frameModal: 'open'}, '', window.location.href);

      if (this.#exitConfirmationRequired(this.panelEl)) {
        this.#openExitConfirmation();
        return;
      }

      this.forceHide = true;
      this.panel.hide();
    };
    window.addEventListener('popstate', this.boundPopState);
  }

  #dispose() {
    const shouldRollbackHistory = this.historyPushed && !this.navigating;

    if (this.boundPopState) {
      window.removeEventListener('popstate', this.boundPopState);
      this.boundPopState = null;
    }

    if (this.panel) {
      this.panel.dispose();
      this.panel = null;
    }
    this.panelEl = null;
    if (this.exitConfirmation) {
      this.exitConfirmation.dispose();
      this.exitConfirmation = null;
    }
    // Bootstrap locks body scroll via inline styles and only releases them in
    // hide()'s async callback; disposing synchronously skips that.
    this.#releaseBodyScrollLock();
    this.#enableTurboCaching();
    this.forceHide = false;
    this.boundHideGuard = null;
    if (this.boundDismissClick) {
      window.removeEventListener('click', this.boundDismissClick, true);
      this.boundDismissClick = null;
    }
    this.boundEscape = null;
    this.element.innerHTML = '';
    this.element.removeAttribute('src');

    if (shouldRollbackHistory) {
      // Drop the two synthetic entries to land back on the page entry.
      this.suppressPopState = true;
      history.go(-2);
      this.historyPushed = false;
      return;
    }

    this.historyPushed = false;

    if (!this.navigating) {
      const url = new URL(window.location.href);
      url.searchParams.delete('finalize');
      url.searchParams.delete('invested');
      Turbo.visit(url.toString(), {action: 'replace'});
    }
  }

  connect() {
    this.navigating = false;
    this.historyPushed = false;
    this.suppressPopState = false;
    this.forceHide = false;
    this.confirming = false;
    this.boundTrack = () => { this.navigating = true; };
    document.addEventListener('turbo:before-visit', this.boundTrack);
  }

  disconnect() {
    document.removeEventListener('turbo:before-visit', this.boundTrack);
    if (this.boundPopState) {
      window.removeEventListener('popstate', this.boundPopState);
      this.boundPopState = null;
    }
    if (this.boundDismissClick) {
      window.removeEventListener('click', this.boundDismissClick, true);
      this.boundDismissClick = null;
    }
    this.#enableTurboCaching();

    // Fallback if the controller is torn down before Bootstrap's async `hidden`
    // event ran #dispose().
    if (this.panel) {
      this.panel.dispose();
      this.panel = null;
      this.panelEl = null;
      if (this.exitConfirmation) {
        this.exitConfirmation.dispose();
        this.exitConfirmation = null;
      }
      this.#releaseBodyScrollLock();
      this.element.innerHTML = '';
      this.element.removeAttribute('src');
    }

    if (this.historyPushed && !this.navigating) {
      this.suppressPopState = true;
      this.historyPushed = false;
      history.go(-2);
    }
  }
}
